import React, { useState, useEffect } from 'react';
import { getArbicsxDashboardStats, getMySubscription } from '../../lib/api';
import { useModule } from '../../contexts/ModuleContext';
import { formatCurrency } from '../../lib/utils';
import { useCountUp } from '../../lib/countUp';
import { RefreshCw, TrendingUp, TrendingDown, Calendar, Clock, BarChart3, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

export default function ArbicsxDashboard() {
  const { impersonatedUser, isImpersonating, isAdmin, isAdminPrimary, isAdminSecondary } = useModule();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [telegramLinked, setTelegramLinked] = useState(true); // Default true to avoid flash
  const [freshUserId, setFreshUserId] = useState(null); // Fresh user ID from backend

  const impersonateId = isImpersonating ? impersonatedUser?.id : null;

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await getArbicsxDashboardStats(impersonateId);
      setStats(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle statistiche');
    } finally {
      setLoading(false);
    }
  };

  // Check if user has Telegram linked (for non-admin_primary, non-impersonating users)
  // Admin_secondary should also see the banner if they don't have Telegram linked
  const checkTelegramStatus = async () => {
    if (isAdminPrimary || isImpersonating) {
      setTelegramLinked(true);
      return;
    }
    try {
      const response = await getMySubscription();
      setTelegramLinked(!!response.data.telegram_chat_id);
      setFreshUserId(response.data.user_id); // Store fresh user ID from backend
    } catch (error) {
      // Silently fail
    }
  };

  useEffect(() => {
    fetchStats();
    checkTelegramStatus();
  }, [impersonateId, isAdminPrimary, isImpersonating]);

  const getValueColor = (value) => {
    if (value === undefined || value === null || value === 0) return 'text-gray-400';
    return value >= 0 ? 'text-success' : 'text-red-500';
  };

  const formatValueWithSign = (value) => {
    if (value === undefined || value === null || value === 0) return '$0';
    const formatted = formatCurrency(Math.abs(value));
    return value >= 0 ? `+${formatted}` : `-${formatted}`;
  };

  // Conteggio cifre: solo al primo caricamento, mai sui refetch/polling/cambio profilo
  const totalProfitView = useCountUp(stats ? (stats.total_profit || 0) : null);
  const todayProfitView = useCountUp(stats ? (stats.today_profit || 0) : null);
  const weekProfitView = useCountUp(stats ? (stats.week_profit || 0) : null);
  const monthProfitView = useCountUp(stats ? (stats.month_profit || 0) : null);

  if (loading && !stats) {
    return (
      <div className="space-y-8" data-testid="arbicsx-dashboard-skeleton">
        <div className="space-y-2">
          <div className="skeleton-box h-8 w-48" />
          <div className="skeleton-box h-4 w-64" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className="stat-card space-y-3">
              <div className="skeleton-box h-4 w-32" />
              <div className="skeleton-box h-8 w-24" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className="stat-card space-y-3">
              <div className="skeleton-box h-4 w-24" />
              <div className="skeleton-box h-7 w-20" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in relative z-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-net-exclude>
        <div className="min-w-0">
          <h1 className="ht-page-h1">Dashboard</h1>
          <p className="ht-page-sub">
            Arbicsx - {isImpersonating ? `Dati di ${impersonatedUser?.name}` : 'I tuoi dati'}
          </p>
        </div>
        <button
          type="button"
          onClick={fetchStats}
          className="ht-btn self-start sm:self-auto"
          disabled={loading}
          data-testid="arbicsx-dashboard-refresh-btn"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Aggiorna
        </button>
      </div>

      {/* Telegram Connection Card - For non-admin_primary users without Telegram linked (includes admin_secondary) */}
      {!isAdminPrimary && !isImpersonating && !telegramLinked && freshUserId && (
        <div className="ht-panel p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-net-exclude>
          <div className="flex items-center gap-3 min-w-0">
            <Send size={18} className="text-[#5f6672] flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="ht-panel-title text-sm">Collega Telegram</h3>
              <p className="text-xs text-[#8b919c]">Ricevi notifiche sui tuoi conti direttamente su Telegram</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => window.open(`https://t.me/HedgingTrakerBot?start=${freshUserId}`, '_blank')}
            className="ht-btn ht-btn-primary justify-center flex-shrink-0"
          >
            <Send size={14} />
            Collega ora
          </button>
        </div>
      )}

      {/* Profitto Totale grande a sinistra, righe sottili a destra */}
      <div className="ht-panel" data-testid="stat-card-total" data-net-exclude>
        <div className="flex flex-col md:flex-row md:items-stretch">
          <div className="p-5 md:p-6 md:w-[40%] md:border-r border-b md:border-b-0 border-[#282c34] flex flex-col justify-center min-w-0">
            <span className="ht-hero-label flex items-center gap-1.5">
              {(stats?.total_profit || 0) >= 0
                ? <TrendingUp size={13} className="ht-pos" />
                : <TrendingDown size={13} className="ht-alert" />}
              Profitto Totale
            </span>
            <p className={`ht-hero-num ${(stats?.total_profit || 0) >= 0 ? 'ht-pos' : 'ht-alert'}`}>
              {formatValueWithSign(totalProfitView || 0)}
            </p>
          </div>
          <div className="flex-1 min-w-0 px-4 py-2 md:px-5 md:py-3 ht-rows">
            <div className="ht-row" data-testid="stat-card-today">
              <span className="ht-row-label">Oggi</span>
              <span className={`ht-row-value ${(stats?.today_profit || 0) > 0 ? 'ht-pos' : (stats?.today_profit || 0) < 0 ? 'ht-alert' : ''}`}>
                {formatValueWithSign(todayProfitView || 0)}
              </span>
            </div>
            <div className="ht-row" data-testid="stat-card-week">
              <span className="ht-row-label">Questa Settimana</span>
              <span className={`ht-row-value ${(stats?.week_profit || 0) > 0 ? 'ht-pos' : (stats?.week_profit || 0) < 0 ? 'ht-alert' : ''}`}>
                {formatValueWithSign(weekProfitView || 0)}
              </span>
            </div>
            <div className="ht-row" data-testid="stat-card-month">
              <span className="ht-row-label">Questo Mese</span>
              <span className={`ht-row-value ${(stats?.month_profit || 0) > 0 ? 'ht-pos' : (stats?.month_profit || 0) < 0 ? 'ht-alert' : ''}`}>
                {formatValueWithSign(monthProfitView || 0)}
              </span>
            </div>
            <div className="ht-row" data-testid="stat-card-active-cycles">
              <span className="ht-row-label">Conti Attivi</span>
              <span className="ht-row-value">{stats?.active_cycles || 0}</span>
            </div>
            <div className="ht-row" data-testid="stat-card-completed-cycles">
              <span className="ht-row-label">Cicli Completati</span>
              <span className="ht-row-value">{stats?.completed_cycles || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
